/* eslint-disable max-lines */
import { signOut as corporateGroupLogout } from 'next-auth/react';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormState } from '../../../../bookings/FormState/useFormState';
import { LinkId } from '../../../AnalyticsContext/LinkId';
import { useCorporateGroupIdentityConfig } from '../../../CorporateGroupIdentityConfigProvider/CorporateGroupIdentityConfigProvider';
import { IdentityProvider } from '../../../UserProvider/UserData';
import { useUserData } from '../../../UserProvider/UserProvider';
import { LoginMethod, useLogin } from '../../../UserProvider/useLogin';
import { useLogout } from '../../../UserProvider/useLogout';
import { useCTA } from '../../../hooks/useCTA';
import { useRouter } from '../../../hooks/useRouter';
import { ButtonVariants } from '../../Button/DeprecatedButtonVariants';
import { CTADot } from '../../CTADot';
import { LanguagePicker } from '../../i18n/LanguagePicker/LanguagePicker';
import { UserAvatar } from '../UserAvatar/UserAvatar';
import {
  Links,
  PrivacyIconContainer,
  StyledLink,
  StyledPrivacyIcon,
  IconHealthEngine,
  LoginWrapper,
  LoginDropdown,
  LoginDropdownItem,
  HoverButton,
  Button,
  DesktopNavMenuContainer,
} from './DesktopNavMenu.styles';
import { IdentityProviderLoginGroup } from './IdentityProviderLoginGroup/IdentityProviderLoginGroup';

interface DesktopNavMenuProps {
  withPracticeCTA?: boolean;
  showExternalActions?: boolean;
  showLoginsAndPolicyPopups?: boolean;
  showLoginDropdown?: boolean;
  searchExperimentRunning?: boolean;
}

export const DesktopNavMenu = ({
  withPracticeCTA = true,
  showExternalActions = true,
  showLoginsAndPolicyPopups = true,
  showLoginDropdown = false,
  searchExperimentRunning = false,
}: DesktopNavMenuProps) => {
  const { data: user, loading: userLoading, identityProvider } = useUserData();

  const router = useRouter();
  const { cta } = useCTA();

  const heLogout = useLogout();

  const logout = useMemo(() => {
    switch (identityProvider) {
      case IdentityProvider.HEALTHENGINE:
        return heLogout;
      case IdentityProvider.CORPORATE_GROUP:
        return corporateGroupLogout;
      default:
        return undefined;
    }
  }, [identityProvider, heLogout]);

  const showMCTNavMenuDot = cta?.showMctCTA && !router?.pathname.includes('my-account');

  return (
    <DesktopNavMenuContainer
      data-testid="desktopNavMenu"
      searchExperimentRunning={searchExperimentRunning}
    >
      {user && (
        <Links>
          {identityProvider === IdentityProvider.HEALTHENGINE && (
            <>
              <StyledLink to="/my-account/bookings/" trackingId={LinkId.HEADER_LOGGED_IN_BOOKING}>
                Bookings
              </StyledLink>
              <StyledLink
                to={`/my-account/favourites${
                  cta?.showMctCTA ? '?icid=nd:man:hea:::all:mct:::jun22' : ''
                }`}
                trackingId={LinkId.HEADER_LOGGED_IN_FAVOURITES}
                data-notify-dot={Boolean(cta?.showMctCTA)}
              >
                <IconHealthEngine color="currentColor" height={16} />
                My Care Team™
                {showMCTNavMenuDot && <CTADot />}
              </StyledLink>
              <HelpLink />
              <PrivacyLink />
              <LanguagePicker />
            </>
          )}
          {identityProvider && (
            <UserAvatar user={user} identityProvider={identityProvider} onClickLogout={logout} />
          )}
        </Links>
      )}
      {((!userLoading && !user) || userLoading) && (
        <LoggedOutLinks
          showExternalActions={showExternalActions}
          showLoginsAndPolicyPopups={showLoginsAndPolicyPopups}
          withPracticeCTA={withPracticeCTA}
          identityProvider={identityProvider}
          showLoginDropdown={showLoginDropdown}
        />
      )}
    </DesktopNavMenuContainer>
  );
};

const LoggedOutLinks = ({
  showLoginsAndPolicyPopups,
  showExternalActions,
  withPracticeCTA,
  identityProvider,
  showLoginDropdown,
}: DesktopNavMenuProps & { identityProvider?: IdentityProvider }) => {
  const { data: identityConfig } = useCorporateGroupIdentityConfig();

  return (
    <Links>
      {withPracticeCTA && (
        <StyledLink
          trackingId={LinkId.HEADER_ARE_YOU_A_PRACTICE}
          to="https://practices.healthengine.com.au/list-your-practice/?pracsource=header-link"
        >
          List your practice, it's free
        </StyledLink>
      )}
      {showExternalActions && <HelpLink />}
      {showLoginsAndPolicyPopups && (
        <>
          <PrivacyLink />
          <LanguagePicker />
          {identityProvider === IdentityProvider.HEALTHENGINE && (
            <LoginButton showLoginDropdown={showLoginDropdown} />
          )}
        </>
      )}
      {identityProvider === IdentityProvider.CORPORATE_GROUP && identityConfig && (
        <IdentityProviderLoginGroup config={identityConfig} />
      )}
    </Links>
  );
};

const PrivacyLink = () => (
  <PrivacyIconContainer
    aria-label="Privacy policy information"
    href="https://privacy.healthengine.com.au/"
    target="_blank"
    data-link-id="Header Privacy Link"
  >
    <StyledPrivacyIcon />
  </PrivacyIconContainer>
);

const HelpLink = () => {
  const { t } = useTranslation();

  return (
    <StyledLink
      trackingId={LinkId.HEADER_HELP}
      to="https://support.healthengine.com.au/hc/en-us/categories/200431715-Patient"
      data-link-id="Header Help"
    >
      {t('button.help')}
    </StyledLink>
  );
};

const LoginButton = ({ showLoginDropdown }: { showLoginDropdown?: boolean }) => {
  const { t } = useTranslation();

  const {
    state: {
      appointment: { bookingFormClient, practice },
    },
  } = useFormState();

  const practiceId = practice?.id;
  const triggerLogin = useLogin(LoginMethod.Email, { practiceId, bookingFormClient });

  const caption = (
    <>
      {t('button.logIn.verb')}
      {' / '}
      {t('button.signUp.verb')}
    </>
  );

  return showLoginDropdown ? (
    <LoginWrapper>
      <HoverButton onClick={triggerLogin} trackingId={LinkId.HEADER_LOGIN_SIGN_UP}>
        {caption}
      </HoverButton>
      <LoginDropdown>
        <LoginDropdownItem to="/members/login">Login as patient</LoginDropdownItem>
        <LoginDropdownItem to="/appointment/admin">Login as practice</LoginDropdownItem>
      </LoginDropdown>
    </LoginWrapper>
  ) : (
    <Button
      variant={ButtonVariants.accent}
      onClick={triggerLogin}
      trackingId={LinkId.HEADER_LOGIN_SIGN_UP}
    >
      {caption}
    </Button>
  );
};
